import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAAL2Verified, setIsAAL2Verified] = useState(false);

  useEffect(() => {
    const checkAAL = async () => {
      try {
        const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        // If they don't have 2FA set up (nextLevel === 'aal1') or they already verified it ('aal2')
        setIsAAL2Verified(data?.currentLevel === 'aal2' || data?.nextLevel === 'aal1');
      } catch (err) {
        setIsAAL2Verified(false);
      }
    };

    const handleSession = async (session: Session | null) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session) {
        // Enforce a strict timeout so local auth loading never hangs indefinitely
        await Promise.race([checkAAL(), new Promise(r => setTimeout(r, 2000))]);
      }
      setLoading(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => handleSession(session)
    );

    supabase.auth.getSession().then(({ data: { session } }) => handleSession(session));

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: window.location.origin,
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return { user, session, loading, isAAL2Verified, signUp, signIn, signOut };
}
